// engine/core/AssetManager.ts
import Phaser from "phaser";
import { apiPath } from "../../../../config/config"
export type AssetSpec =
    | { kind: "image"; key: string; url: string }
    | { kind: "audio"; key: string; url: string };

export class AssetManager {
    private scene: Phaser.Scene;
    private queued = new Map<string, AssetSpec>(); // dédoublonne par key
    private loaded = new Set<string>();

    constructor(scene: Phaser.Scene) {
        this.scene = scene;

        // Autoriser CORS si le serveur les envoie
        (this.scene.load as any).crossOrigin = "anonymous";

        // Déduire host et prefix depuis apiPath (ex: https://host/prefix)
        let baseHost = "";
        let basePathPrefix = "";
        if (apiPath) {
            try {
                const u = new URL(apiPath);
                baseHost = u.hostname;
                basePathPrefix = (u.pathname || "").replace(/\/+$/, ""); // sans trailing slash
                this.scene.load.setBaseURL(apiPath.replace(/\/$/, ""));   // base pour les URLs relatives "public/..."
            } catch {
                // apiPath n'est pas une URL absolue → on laisse Phaser gérer les chemins relatifs
            }
        }

        // Mémoriser pour normalizeUrl()
        (this as any)._baseHost = baseHost;
        (this as any)._pathPrefix = basePathPrefix;

        // ✅ FILE_COMPLETE : l’event fournit `key` (pas un objet file)
        this.scene.load.on(Phaser.Loader.Events.FILE_COMPLETE, (key: string) => {
            if (key) this.loaded.add(key);
        });

        // Logs d’erreurs (URL finale)
        this.scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: any) => {
            console.warn("[FILE_LOAD_ERROR]", file?.key, file?.src || file?.url);
        });
    }

    public resolveUrl(raw: string): string {
        // expose la normalisation (absolue/relative + prefix apiPath)
        // pour les cas où on doit appeler this.load.spritesheet directement
        // (spritesheet n'a pas de "ensure" ici)
        // NB: this.normalizeUrl existe déjà en privé
        // @ts-ignore - on utilise la méthode privée
        return (this as any).normalizeUrl(raw);
    }
    private normalizeUrl(raw: string): string {
        const u = String(raw ?? "").trim();
        if (!u) return u;

        const baseHost: string = (this as any)._baseHost || "";
        const pathPrefix: string = (this as any)._pathPrefix || ""; // ex: "/tc_tilmicodebe"

        // Si l'URL est absolue et même host que apiPath, injecter le prefix si absent
        try {
            const abs = new URL(u);
            if (pathPrefix && baseHost && abs.hostname === baseHost) {
                const needsPrefix =
                    !abs.pathname.startsWith(pathPrefix + "/") && abs.pathname !== pathPrefix;
                if (needsPrefix) {
                    abs.pathname =
                        pathPrefix.replace(/\/+$/, "") + "/" + abs.pathname.replace(/^\/+/, "");
                }
            }
            return abs.toString();
        } catch {
            // Relative → Phaser résout contre baseURL; on enlève juste le leading slash
            return u.replace(/^\//, "");
        }
    }



    /** Ajoute un asset au lot de preload (sans démarrer le loader). */
    public queue(spec: AssetSpec) {
        if (!spec?.key) return;
        // si déjà chargé, ne requeue pas
        if (this.isLoaded(spec.key)) return;
        this.queued.set(spec.key, spec);
    }

    /** Démarre un unique preload pour tout ce qui a été queué. */
    public preloadAll(onComplete?: () => void) {
        if (this.scene.load.isLoading()) {
            this.scene.load.once(Phaser.Loader.Events.COMPLETE, () => this.preloadAll(onComplete));
            return;
        }

        if (this.queued.size === 0) {
            onComplete?.();
            return;
        }

        const done = () => {
            this.scene.load.off(Phaser.Loader.Events.COMPLETE, done);
            onComplete?.();
        };
        this.scene.load.on(Phaser.Loader.Events.COMPLETE, done);

        for (const spec of this.queued.values()) {
            const url = this.normalizeUrl(spec.url);
            if (spec.kind === "image") this.scene.load.image(spec.key, url);
            else if (spec.kind === "audio") this.scene.load.audio(spec.key, url);
        }
        this.queued.clear();

        if (!this.scene.load.isLoading()) this.scene.load.start();
    }


    /** Déjà dispo en cache textures/sons ? */
    public isLoaded(key: string): boolean {
        const hasTex = this.scene.textures.exists(key);
        const hasAud = this.scene.cache?.audio?.exists?.(key) ?? false; // ✅
        return this.loaded.has(key) || hasTex || hasAud;
    }

    public ensureImage(key: string, url: string, onComplete?: () => void) {
        if (this.isLoaded(key)) return onComplete?.();
        const finalUrl = this.normalizeUrl(url);
        this.scene.load.once(Phaser.Loader.Events.COMPLETE, () => onComplete?.());
        this.scene.load.image(key, finalUrl);
        this.scene.load.start();
    }

    public ensureAudio(key: string, url: string, onComplete?: () => void) {
        if (this.isLoaded(key)) return onComplete?.();
        const finalUrl = this.normalizeUrl(url);
        this.scene.load.once(Phaser.Loader.Events.COMPLETE, () => onComplete?.());
        this.scene.load.audio(key, finalUrl);
        this.scene.load.start();
    }
}
